import { useCallback, useEffect, useState } from 'react'

import { fetchBlobUrl } from '../../lib/api'
import { errorText } from '../../lib/offsite'
import { useSession } from '../../lib/SessionContext'
import { SectionHead } from '../offsite/parts'

/* ============================================================================
   สิทธิการลารายปี (HR_CAN_LEAVE)
   API: backend/src/routes/adminLeaveQuota.js · lib/leaveQuota.js

   ── ทำไมต้องมีหน้านี้ ──────────────────────────────────────────────────────
   แทนสองเมนูของโปรแกรม WinForms เดิม (HR_Tech):
     frmProcessLeave → คิดโควตาทั้งบริษัทรายปี
     frmHRCanLeave   → แก้รายคน

   หน้า "ข้อมูลบุคคล" อ่านตารางนี้มาแสดงสิทธิลาอยู่แล้ว แต่ก่อนหน้านี้ไม่มีที่ไหน
   **แก้** ได้เลย ถ้าปิดโปรแกรมเดิมจะตั้งโควตาปีถัดไปไม่ได้

   ── สูตรคิดสิทธิ ───────────────────────────────────────────────────────────
   port มาจาก HR_Tech/calLeave.cs ทุกกิ่ง ไม่ได้ออกแบบใหม่
   ยืนยันแล้วว่าคิดแถวปี 2026 ที่โปรแกรมเดิมลงไว้ 226 แถว ได้ตรงกันทั้งหมด
   **อย่าแก้สูตรเองโดยไม่มีใครสั่ง** ตัวเลขนี้คือสิทธิพนักงานจริง
   ============================================================================ */

type Quota = {
  empId: string
  empName: string
  leaveType: string
  leaveTypeDesc: string | null
  year: number
  companyCode: string
  dayCanUsed: number
  dayHadUsed: number
  remaining: number
  startDate: string | null
  workStartDate: string | null
}

type PreviewItem = {
  empId: string
  empName: string
  workStartDate: string
  leaveType: string
  leaveTypeDesc: string
  serviceYears: number
  days: number
  add: number
  total: number
  startDate: string
}

type Preview = {
  year: number
  items: PreviewItem[]
  unsupported: { empId: string; empName: string; leaveTypeDesc: string }[]
}

/* นำเข้าโควตาจาก Excel (ประเภท Walk & Run ฯลฯ ที่สูตรคิดให้ไม่ได้) */
type ImportRow = { empId: string; empName: string; days: number }
type ImportError = { rowNo: number; code: string; empName?: string; reason: string }
type ImportResult = {
  leaveType: string
  year: number
  ok: ImportRow[]
  duplicate: ImportRow[]
  errors: ImportError[]
  counts: { toInsert: number; duplicate: number; error: number }
  applied?: boolean
  inserted?: number
  updated?: number
}

/** ประเภทลาที่ตั้งโควตาด้วย import ได้ — ตอนนี้มีแค่ Walk & Run (006) */
const IMPORT_LEAVE_TYPE = '006'
const IMPORT_LEAVE_LABEL = 'Walk & Run'
const MAX_FILE_MB = 5

const BASE = '/api/admin/leave-quota'
const nf = new Intl.NumberFormat('th-TH')

/** อ่านไฟล์เป็น base64 data URL (แบบเดียวกับ Slides/News) */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'))
    r.readAsDataURL(file)
  })
}

/** ปีให้เลือก — ปีนี้ ±2 พอสำหรับงานจริง (ตั้งล่วงหน้าปีหน้า ย้อนดูปีก่อน) */
function yearOptions() {
  const now = new Date().getUTCFullYear()
  return [now - 2, now - 1, now, now + 1, now + 2]
}

export default function LeaveQuota() {
  const { call, state } = useSession()
  /* ซ่อนปุ่มที่กดไปก็โดนปฏิเสธ — backend เป็นคนบังคับสิทธิ์จริง (canWrite ใน
     adminLeaveQuota.js) ตรงนี้แค่ไม่ให้เห็นปุ่มที่ใช้ไม่ได้ · viewer อ่านได้อย่างเดียว */
  const canWrite =
    state.status === 'signed-in' &&
    state.session.kind === 'admin' &&
    ['superadmin', 'admin', 'hr'].includes(state.session.role)

  const [year, setYear] = useState(() => new Date().getUTCFullYear())
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [rows, setRows] = useState<Quota[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [preview, setPreview] = useState<Preview | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [genError, setGenError] = useState<string | null>(null)

  /** แถวที่กำลังแก้ — เก็บค่าที่พิมพ์แยกไว้ ไม่เขียนทับตารางจนกว่าจะบันทึก */
  const [editing, setEditing] = useState<{ key: string; days: string } | null>(null)

  /* นำเข้าโควตาจาก Excel */
  const [impFile, setImpFile] = useState<{ data: string; name: string } | null>(null)
  const [impPreview, setImpPreview] = useState<ImportResult | null>(null)
  const [impBusy, setImpBusy] = useState(false)
  const [impError, setImpError] = useState<string | null>(null)
  const [impNotice, setImpNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ year: String(year) })
      if (q) qs.set('q', q)
      const r = await call<{ quotas: Quota[] }>(`${BASE}?${qs}`)
      setRows(r.quotas)
      setError(null)
    } catch (err) {
      setError(errorText(err))
      setRows(null)
    } finally {
      setLoading(false)
    }
  }, [call, year, q])

  useEffect(() => {
    void load()
  }, [load])

  const doPreview = async () => {
    setBusy(true)
    setGenError(null)
    setNotice(null)
    try {
      setPreview(await call<Preview>(`${BASE}/preview?year=${year}`))
    } catch (err) {
      setGenError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const doGenerate = async () => {
    setBusy(true)
    setGenError(null)
    try {
      const r = await call<{ inserted: number; year: number }>(`${BASE}/generate`, {
        method: 'POST',
        body: JSON.stringify({ year }),
      })
      setNotice(`คิดสิทธิปี ${r.year} แล้ว — เพิ่ม ${nf.format(r.inserted)} รายการ`)
      setPreview(null)
      await load()
    } catch (err) {
      setGenError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const saveDays = async (row: Quota, days: string) => {
    setBusy(true)
    setError(null)
    try {
      await call(`${BASE}/${encodeURIComponent(row.empId)}/${encodeURIComponent(row.leaveType)}/${row.year}`, {
        method: 'PATCH',
        body: JSON.stringify({ days: Number(days) }),
      })
      setEditing(null)
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  const removeRow = async (row: Quota) => {
    if (
      !window.confirm(
        `ลบสิทธิ "${row.leaveTypeDesc ?? row.leaveType}" ปี ${row.year} ของ ${row.empId} ${row.empName}?\n` +
          'ลบแล้วคนนี้จะกลับไปอยู่ในรายการที่รอคิดสิทธิใหม่',
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      await call(`${BASE}/${encodeURIComponent(row.empId)}/${encodeURIComponent(row.leaveType)}/${row.year}`, {
        method: 'DELETE',
      })
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  /** ดาวน์โหลด template Excel (รายชื่อ active + ช่องจำนวนวัน) */
  const downloadTemplate = async () => {
    setImpError(null)
    try {
      const url = await fetchBlobUrl(
        `${BASE}/export?leaveType=${IMPORT_LEAVE_TYPE}&year=${year}`,
      )
      const a = document.createElement('a')
      a.href = url
      a.download = `leave-quota-${IMPORT_LEAVE_TYPE}-${year}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setImpError(errorText(err))
    }
  }

  const onPickImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // ให้เลือกไฟล์เดิมซ้ำได้
    if (!file) return
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setImpError(`ไฟล์ใหญ่เกิน ${MAX_FILE_MB} MB`)
      return
    }
    setImpError(null)
    setImpNotice(null)
    setImpPreview(null)
    setImpBusy(true)
    try {
      const data = await readFileAsDataUrl(file)
      setImpFile({ data, name: file.name })
      // ดูผลก่อนทันที (ยังไม่เขียนอะไร)
      const pv = await call<ImportResult>(`${BASE}/import`, {
        method: 'POST',
        body: JSON.stringify({
          fileName: file.name,
          content: data,
          leaveType: IMPORT_LEAVE_TYPE,
          year,
        }),
      })
      setImpPreview(pv)
    } catch (err) {
      setImpError(errorText(err))
      setImpFile(null)
    } finally {
      setImpBusy(false)
    }
  }

  const applyImport = async () => {
    if (!impFile) return
    setImpBusy(true)
    setImpError(null)
    try {
      const r = await call<ImportResult>(`${BASE}/import`, {
        method: 'POST',
        body: JSON.stringify({
          fileName: impFile.name,
          content: impFile.data,
          leaveType: IMPORT_LEAVE_TYPE,
          year,
          apply: true,
        }),
      })
      setImpNotice(
        `นำเข้าแล้ว — เพิ่ม ${nf.format(r.inserted ?? 0)} รายการ` +
          (r.updated ? ` · แก้ค่าเดิม ${nf.format(r.updated)}` : '') +
          (r.counts.error ? ` · ข้าม error ${nf.format(r.counts.error)}` : ''),
      )
      setImpPreview(null)
      setImpFile(null)
      await load()
    } catch (err) {
      setImpError(errorText(err))
    } finally {
      setImpBusy(false)
    }
  }

  const list = rows ?? []

  return (
    <>
      <h1>สิทธิการลา</h1>
      <p className="meta">
        โควตาวันลาของแต่ละคนในแต่ละปี — หน้า <strong>ข้อมูลบุคคล</strong> อ่านตัวเลขนี้ไปแสดง
        · สูตรคิดยกมาจากโปรแกรมเดิมทั้งหมด ตรวจแล้วว่าได้ตัวเลขตรงกัน
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      {canWrite && (
        <section className="card">
          <h2>คิดสิทธิประจำปี</h2>
          <p className="meta">
            คิดให้เฉพาะคนที่<strong>ยังไม่มีสิทธิของปีนั้น</strong> — คนที่มีอยู่แล้วจะไม่ถูกแตะ
            ตั้งซ้ำกี่ครั้งก็ไม่เกิดรายการซ้ำ
          </p>

          <form className="toolbar" onSubmit={(e) => e.preventDefault()}>
            <label>
              ปี
              <select
                value={year}
                onChange={(e) => {
                  setYear(Number(e.target.value))
                  setPreview(null)
                  setNotice(null)
                }}
              >
                {yearOptions().map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={() => void doPreview()} disabled={busy}>
              {busy ? 'กำลังคิด…' : 'ดูผลก่อน'}
            </button>
            {preview && (
              <button type="button" className="ghost" onClick={() => setPreview(null)} disabled={busy}>
                ล้าง
              </button>
            )}
          </form>

          {genError && <p className="error">{genError}</p>}
          {notice && <p className="ok">{notice}</p>}

          {preview && (
            <>
              {preview.items.length === 0 ? (
                <p className="meta">ปี {preview.year} ทุกคนมีสิทธิครบแล้ว ไม่มีอะไรต้องเพิ่ม</p>
              ) : (
                <>
                  <p className="meta">
                    จะเพิ่ม <strong>{nf.format(preview.items.length)}</strong> รายการ
                    {' · รวม '}
                    {nf.format(preview.items.reduce((s, i) => s + i.total, 0))} วัน
                  </p>

                  {preview.unsupported.length > 0 && (
                    <p className="warn">
                      มี <strong>{preview.unsupported.length} รายการ</strong>ที่สูตรคิดให้ไม่ได้ และจะถูกข้าม —
                      ต้องตั้งด้วยมือเอง (ประเภทลาที่ไม่ใช่ลากิจ/ลาพักร้อน หรือยังไม่ได้กรอกวันเริ่มงาน)
                    </p>
                  )}

                  <div className="table-wrap table-wrap--rows10">
                    <table className="data">
                      <thead>
                        <tr>
                          <th>รหัส</th>
                          <th>ชื่อ</th>
                          <th>ประเภท</th>
                          <th className="nowrap">เริ่มงาน</th>
                          <th>อายุงาน</th>
                          <th>พื้นฐาน</th>
                          <th>บวกอายุงาน</th>
                          <th>รวม</th>
                          <th className="nowrap">ใช้ได้ตั้งแต่</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.items.map((i) => (
                          <tr key={`${i.empId}-${i.leaveType}`}>
                            <td className="nowrap">{i.empId}</td>
                            <td>{i.empName}</td>
                            <td className="nowrap">{i.leaveTypeDesc}</td>
                            <td className="nowrap">{i.workStartDate}</td>
                            <td>{i.serviceYears} ปี</td>
                            <td>{i.days}</td>
                            <td>{i.add > 0 ? `+${i.add}` : '-'}</td>
                            <td>
                              <strong>{i.total}</strong>
                            </td>
                            <td className="nowrap">{i.startDate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p>
                    <button type="button" onClick={() => void doGenerate()} disabled={busy}>
                      ยืนยันคิดสิทธิ {nf.format(preview.items.length)} รายการ
                    </button>
                  </p>
                </>
              )}
            </>
          )}
        </section>
      )}

      {canWrite && (
        <section className="card">
          <h2>นำเข้าโควตาจาก Excel · {IMPORT_LEAVE_LABEL}</h2>
          <p className="meta">
            สำหรับประเภทที่สูตรคิดให้ไม่ได้ (เช่น {IMPORT_LEAVE_LABEL}) — ตั้งโควตารายคนด้วย Excel ·
            <strong> ใช้ปีที่เลือกด้านบน (ปี {year})</strong> · คนใหม่จะเพิ่มให้ คนที่มีสิทธิอยู่แล้วจะแก้เป็นค่าใหม่
            (ไม่แตะวันที่ใช้ไปแล้ว)
          </p>

          <div className="toolbar">
            <button type="button" className="ghost" onClick={() => void downloadTemplate()} disabled={impBusy}>
              ⬇ ดาวน์โหลด template (รายชื่อพนักงานที่ยังทำงาน)
            </button>
            <label className="btn-file">
              เลือกไฟล์ Excel ที่กรอกแล้ว
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => void onPickImport(e)}
                disabled={impBusy}
                hidden
              />
            </label>
          </div>

          {impBusy && <p className="meta">กำลังอ่านไฟล์…</p>}
          {impError && <p className="error">{impError}</p>}
          {impNotice && <p className="ok">{impNotice}</p>}

          {impPreview && (
            <>
              <p className="meta">
                ไฟล์นี้จะเพิ่ม <strong>{nf.format(impPreview.counts.toInsert)}</strong> รายการ
                {impPreview.counts.duplicate > 0 && (
                  <> · แก้ค่าเดิม {nf.format(impPreview.counts.duplicate)}</>
                )}
                {impPreview.counts.error > 0 && (
                  <>
                    {' · '}
                    <strong className="bad">ปัญหา {nf.format(impPreview.counts.error)} แถว</strong>
                  </>
                )}
              </p>

              {impPreview.errors.length > 0 && (
                <div className="table-wrap table-wrap--rows10">
                  <table className="data">
                    <thead>
                      <tr>
                        <th className="nowrap">แถว</th>
                        <th className="nowrap">รหัส</th>
                        <th>ชื่อ</th>
                        <th>ปัญหา</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impPreview.errors.map((er) => (
                        <tr key={`${er.rowNo}-${er.code}`}>
                          <td className="nowrap">{er.rowNo}</td>
                          <td className="nowrap">{er.code || '-'}</td>
                          <td>{er.empName ?? <span className="meta">—</span>}</td>
                          <td className="bad">{er.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {impPreview.ok.length > 0 && (
                <div className="table-wrap table-wrap--rows10">
                  <table className="data">
                    <thead>
                      <tr>
                        <th className="nowrap">รหัส</th>
                        <th>ชื่อ</th>
                        <th>จำนวนวัน</th>
                      </tr>
                    </thead>
                    <tbody>
                      {impPreview.ok.map((r) => (
                        <tr key={r.empId}>
                          <td className="nowrap">{r.empId}</td>
                          <td>{r.empName}</td>
                          <td>
                            <strong>{r.days}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {impPreview.duplicate.length > 0 && (
                <>
                  <p className="meta">แก้ค่าเดิม (มีสิทธิปีนี้อยู่แล้ว):</p>
                  <div className="table-wrap table-wrap--rows10">
                    <table className="data">
                      <thead>
                        <tr>
                          <th className="nowrap">รหัส</th>
                          <th>ชื่อ</th>
                          <th>จำนวนวันใหม่</th>
                        </tr>
                      </thead>
                      <tbody>
                        {impPreview.duplicate.map((r) => (
                          <tr key={r.empId}>
                            <td className="nowrap">{r.empId}</td>
                            <td>{r.empName}</td>
                            <td>
                              <strong>{r.days}</strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {impPreview.counts.toInsert + impPreview.counts.duplicate > 0 ? (
                <p>
                  <button type="button" onClick={() => void applyImport()} disabled={impBusy}>
                    ยืนยัน (เพิ่ม {nf.format(impPreview.counts.toInsert)}
                    {impPreview.counts.duplicate > 0 && ` · แก้ ${nf.format(impPreview.counts.duplicate)}`})
                  </button>{' '}
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => {
                      setImpPreview(null)
                      setImpFile(null)
                    }}
                    disabled={impBusy}
                  >
                    ยกเลิก
                  </button>
                </p>
              ) : (
                <p className="meta">ไม่มีรายการให้บันทึก (ยังไม่ได้กรอกจำนวนวัน หรือมีแต่แถวที่ผิด)</p>
              )}
            </>
          )}
        </section>
      )}

      <section className="card">
        <SectionHead
          title={`สิทธิที่ลงไว้ ปี ${year}`}
          meta={rows ? `${nf.format(list.length)} รายการ` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setQ(term.trim())
          }}
        >
          <label>
            ปี
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
        </form>

        {rows === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : list.length === 0 ? (
          <p className="meta">ยังไม่มีสิทธิการลาของปี {year}</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>ชื่อ</th>
                  <th>ประเภท</th>
                  <th>สิทธิ (วัน)</th>
                  <th>ใช้ไป</th>
                  <th>คงเหลือ</th>
                  <th className="nowrap">ใช้ได้ตั้งแต่</th>
                  {canWrite && <th />}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => {
                  const key = `${r.empId}-${r.leaveType}`
                  const isEditing = editing?.key === key
                  return (
                    <tr key={key}>
                      <td className="nowrap">{r.empId}</td>
                      <td>{r.empName}</td>
                      <td className="nowrap">{r.leaveTypeDesc ?? r.leaveType}</td>
                      <td>
                        {isEditing ? (
                          <input
                            type="number"
                            min={0}
                            max={60}
                            step="0.5"
                            value={editing.days}
                            autoFocus
                            style={{ width: '5rem' }}
                            onChange={(e) => setEditing({ key, days: e.target.value })}
                          />
                        ) : (
                          <strong>{r.dayCanUsed}</strong>
                        )}
                      </td>
                      {/* ใช้ไปแล้ว มาจากใบลาที่อนุมัติจริง แก้ที่นี่ไม่ได้โดยตั้งใจ */}
                      <td className="meta">{r.dayHadUsed}</td>
                      <td>{r.remaining}</td>
                      <td className="nowrap meta">{r.startDate ?? '-'}</td>
                      {canWrite && (
                        <td className="nowrap">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="ghost tiny"
                                disabled={busy}
                                onClick={() => void saveDays(r, editing.days)}
                              >
                                บันทึก
                              </button>{' '}
                              <button
                                type="button"
                                className="ghost tiny"
                                disabled={busy}
                                onClick={() => setEditing(null)}
                              >
                                ยกเลิก
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="ghost tiny"
                                disabled={busy}
                                onClick={() => setEditing({ key, days: String(r.dayCanUsed) })}
                              >
                                แก้
                              </button>{' '}
                              <button
                                type="button"
                                className="ghost tiny"
                                disabled={busy}
                                onClick={() => void removeRow(r)}
                              >
                                ลบ
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
